import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './App.css';
import postsData from './data/posts.json';

function BlogPost() {
  const { slug } = useParams(); // Get the slug from the URL
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the specific blog post from static data
  const loadBlogPost = () => {
    try {
      setLoading(true);

      // Find the post with matching slug from static data
      const foundPost = postsData.blogPosts.find(p => p.slug === slug);

      if (!foundPost) {
        throw new Error('Blog post not found');
      }

      setPost(foundPost);
    } catch (err) {
      console.error('Error loading blog post:', err);
      setError('Failed to load blog post. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <p>Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error-container">
          <p>{error}</p>
          <Link to="/">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="App">
        <div className="error-container">
          <p>Blog post not found.</p>
          <Link to="/">← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Header Section - Same as main page */}
      <header 
        className="main-header"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/new-flowers.png)`
        }}
      >
        <div className="header-content">
          <div className="logo-section">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <img src="/images/dm-logo.png" alt="DM Logo" className="logo" />
            </Link>
            <div className="brand-text">
              <h1>
                <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <span className="first-name">DARIA</span><span className="last-name">MARMER</span>
                </Link>
              </h1>
              <p className="tagline">
                PRODUCT <strong>MANAGEMENT</strong> | PRODUCT <strong>STRATEGY</strong> | PRODUCT <strong>LOVE</strong>
              </p>
            </div>
          </div>
        </div>
        
        <nav className="main-nav">
          <ul>
            <li><Link to="/">HOME</Link></li>
            <li><Link to="/about">ABOUT</Link></li>
          </ul>
        </nav>
      </header>

      {/* Blog Post Content */}
      <main className="main-content">
        <div className="container">
          <article className="blog-post-full">
            {/* Back to home link */}
            <div className="back-link">
              <Link to="/">← Back to All Posts</Link>
            </div>
            
            {/* Post header */}
            <header className="post-header">
              <h1 className="post-title">{post.title}</h1>
              <div className="post-meta">
                <span className="post-date">{post.date}</span>
                <span className="post-read-time">{post.readTime}</span>
              </div>
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="post-tags">
                  {post.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>
            
            {/* Featured image */}
            {post.image && (
              <div className="post-image">
                <img 
                  src={post.image} 
                  alt={post.title}
                  onError={(e) => {
                    e.target.src = '/images/pasted image 0.png';
                  }}
                />
              </div>
            )}
            
            {/* Post content */}
            <div className="post-content">
                {post.content.split('\n').map((paragraph, index) => {
                      console.log(`Paragraph ${index}:`, JSON.stringify(paragraph)); // Debug line

                // Handle paragraphs that contain HTML tags
                if (paragraph.includes('<img')) {
                    return <div key={index} dangerouslySetInnerHTML={{ __html: paragraph.trim() }} />;
                }


                // Handle markdown headers
                if (paragraph.trim().startsWith('###')) {
                  const headerText = paragraph.trim().slice(3).trim();
                  return <h3 key={index} className="content-header-h3">{headerText}</h3>;
                }

                if (paragraph.trim().startsWith('##')) {
                  const headerText = paragraph.trim().slice(2).trim();
                  return <h2 key={index} className="content-header-h2">{headerText}</h2>;
                }

                // Handle legacy headers (text that starts with **)
                if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                  const headerText = paragraph.trim().slice(2, -2);
                  return <h3 key={index} className="content-header-h3">{headerText}</h3>;
                }
                
                // Handle empty lines
                if (paragraph.trim() === '') {
                  return <br key={index} />;
                }
                
                // Handle regular paragraphs
                return (
                  <p key={index} className="content-paragraph">
                    {paragraph.split('**').map((part, partIndex) => {
                      // Bold text handling
                      if (partIndex % 2 === 1) {
                        return <strong key={partIndex}>{part}</strong>;
                      }
                      return part;
                    })}
                  </p>
                );
              })}
              
              {!post.content && (
                <p><em>This is a preview. Full blog post content would go here.</em></p>
              )}
            </div>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <p>&copy; 2025 Daria Marmer. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default BlogPost;